<script setup lang="ts">
  import { useFlowStore } from '@/store/flow.store'
  import { axiosClient } from '@/utils/axios-client'
  import {
    FormFieldContainer,
    InputField,
    UiButton,
    useToastStore,
    useXPathAction,
    type FetchElement,
  } from '@logistiek-zonder-papier/lzp-library'
  import { reactive, ref } from 'vue'
  import FlowPathInput from '@/components/form/FlowPathInput.vue'
  import HiddenFieldsManager from '@/components/form-wizard/HiddenFieldsManager.vue'
  import axios from 'axios'
  const field = defineModel<FetchElement>({ required: true })
  const toastStore = useToastStore()
  const flowStore = useFlowStore()
  const { getValue } = useXPathAction()
  const testing = ref(false)

  const probe = axios.create({
    baseURL: axiosClient.defaults.baseURL ?? '',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem('jwt_token')}`,
    },
  })

  const touched = reactive({
    name: false,
    url: false,
    storeLocation: false,
  })
  function validateRequired(key: 'name' | 'url' | 'storeLocation', message: string) {
    return touched[key] && field.value?.type === 'fetch' && !field.value?.[key]?.trim()
      ? message
      : null
  }
  async function test() {
    if (!field.value.url?.trim() || !field.value.storeLocation?.trim()) {
      toastStore.error('URL en opslaglocatie zijn vereist om te testen')
      return
    }
    testing.value = true
    try {
      const resolvedHeaders = Object.fromEntries(
        Object.entries(field.value.headers ?? {}).map(([key, value]) => [
          key,
          getValue(value) ?? value,
        ])
      )

      const response = await probe.request<unknown>({
        url: `${getValue(field.value.url)}`,
        method: 'GET',
        headers: {
          ...resolvedHeaders,
        },
      })
      flowStore.setValue(field.value.storeLocation, response.data)
      toastStore.success('Fetch geslaagd, resultaat opgeslagen in de context')
    } catch {
      toastStore.error('Fetch mislukt')
    } finally {
      testing.value = false
    }
  }
</script>
<template>
  <div class="field-config-container">
    <FormFieldContainer label="Name" :error="validateRequired('name', 'Name is required')">
      <FlowPathInput
        placeholder="Fetch name"
        hint="Identifier for this fetch step."
        v-model="field.name"
        :optional="false"
        type="text"
        :validate-paths="true"
        @blur="touched.name = true"
      />
    </FormFieldContainer>
    <FormFieldContainer label="URL" :error="validateRequired('url', 'URL is required')">
      <FlowPathInput
        placeholder="https://…"
        hint="Endpoint to fetch from."
        v-model="field.url"
        type="text"
        :optional="false"
        :validate-paths="true"
        @blur="touched.url = true"
      />
    </FormFieldContainer>
    <FormFieldContainer
      label="Store location"
      :error="validateRequired('storeLocation', 'Store location is required')"
    >
      <InputField
        placeholder="${context.path.to.store}"
        hint="Where the fetched result is stored."
        v-model="field.storeLocation"
        type="text"
        @blur="touched.storeLocation = true"
      />
    </FormFieldContainer>
    <FormFieldContainer label="Headers">
      <HiddenFieldsManager v-model="field.headers" />
    </FormFieldContainer>
    <div class="test-row">
      <UiButton label="Test" :disabled="testing" @click="test" />
    </div>
  </div>
</template>
<style scoped>
  .field-config-container {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }
  .test-row {
    display: flex;
    justify-content: flex-end;
    margin-top: 0.25rem;
  }
</style>
